import React, { useEffect, useRef, useState } from 'react';

import Appearance from 'styles/Appearance.js';
import { FloatingMenuIndex } from 'structure/Layer.js';
import PageControl from 'views/PageControl.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import Views, { AltBadge } from 'views/Main.js';

const AppointmentLookupField = ({ appendContent, containerStyle, icon, inline, onChange, placeholder, utils, value }) => {

    const limit = 5;
    const offset = useRef(0);

    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(false);
    const [text, setText] = useState(null);

    const onClick = async appointment => {

        // empty results array, set selected appointment, and update text field value
        setResults([]);
        setSelected(appointment);
        setText(appointment.full_name);

        // notify subscribers of new selection
        if(typeof(onChange) === 'function') {
            onChange(appointment);
        }
    }

    const onTextChange = text => {
        setText(text);
        fetchResults(text);
    }

    const getStyles = () => {
        if(inline === false) {
            return {
                backgroundColor: window.theme === 'dark' ? 'rgba(100,100,100,1)' : 'white',
                border: `1px solid rgba(175,175,175,0.25)`,
                borderRadius: 8,
                boxShadow: window.theme === 'dark' ? '4px 4px 7px rgba(174,174,174,0.25)' : '4px 4px 7px rgba(0,0,0,0.15)',
                left: 0,
                maxHeight: 250,
                overflowY: 'scroll',
                padding: 0,
                position: 'absolute',
                right: 0,
                textAlign: 'left',
                top: 43,
                zIndex: FloatingMenuIndex,
            }
        }
        return {
            backgroundColor: window.theme === 'dark' ? 'rgba(100,100,100,1)' : 'white',
            border: `1px solid ${Appearance.colors.divider()}`,
            borderRadius: 12,
            marginBottom: 12,
            marginTop: 8,
            overflow: 'hidden',
            padding: 0,
            textAlign: 'left'
        }
    }

    const fetchResults = async text => {
        try {

            // prevent moving forward if an invalid amount of text was provided
            if(!text || text.length < 3) {

                // end loading and empty results array
                setLoading(false);                    
                setResults([]);

                // notify subscribers that the selected lead has be deselected
                if(typeof(onChange) === 'function') {
                    onChange(null);
                }
                return;
            }

            // submit request to server
            setLoading(true);
             let { appointments, paging } = await Request.get(utils, '/appointments/', {
                limit: limit,
                offset: offset.current,
                search_text: text,
                sort_key: 'start_date',
                sort_type: 3,
                type: 'all'
            });
            
            // end loading and set results object
            setLoading(false);
            setPaging(paging);
            setResults(appointments);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue completing your search. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        if(value) {
            setSelected(value);
            setText(value.full_name);
        }
    }, [value]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            ...containerStyle
        }}>
            <TextField
            appendContent={appendContent}
            autoCapitalize={'sentences'}
            autoComplete={false}
            autoCorrect={false}
            icon={icon || 'search'}
            loading={loading}
            onChange={onTextChange}
            placeholder={placeholder}
            useDelay={true}
            value={text}
            style={{
                flexGrow: 1,
                ...Appearance.textStyles.title()
            }}/>
            {results.length > 0 && (
                <div style={getStyles()}>
                    {results.map((appointment, index) => {
                        return (
                            Views.entry({
                                bottomBorder: index !== results.length - 1,
                                hideIcon: true,
                                key: index,
                                onClick: onClick.bind(this, appointment),
                                title: appointment.full_name,
                                subTitle: `Scheduled for ${Utils.formatDate(appointment.start_date)}`,
                                rightContent: (
                                    <AltBadge content={appointment.status} />
                                )
                            })
                        )
                    })}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset.current}
                        onClick={props => {
                            offset.current = props;
                            fetchResults(text);
                        }} />
                    )}
                </div>
            )}
            {!selected && typeof(text) === 'string' && text.length > 0 && results.length === 0 && loading === false && (
                <div style={getStyles()}>
                    {Views.entry({
                        bottomBorder: false,
                        hideIcon: true,
                        subTitle: 'Nothing was found for your search',
                        title: 'No Appointments Found'
                    })}
                </div>
            )}
        </div>
    )
}
export default AppointmentLookupField;
